import { AutoFixHigh, Create } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/system';
import React, { FC } from 'react';

import { PromptBuilderMode } from '../../Types/enums';

interface PromptBuilderProps {
  mode: PromptBuilderMode;
  onQuickCreate: () => void;
  onStepByStepCreate: () => void;
  onCreateExternalPrompt: () => void;
}

const StyledContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  textAlign: 'left',
  gap: theme.spacing(1.5),
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const PromptBuilder: FC<PromptBuilderProps> = ({
  mode,
  onQuickCreate,
  onStepByStepCreate,
  onCreateExternalPrompt,
}) => {
  const showInternal = mode === PromptBuilderMode.FULL || mode === PromptBuilderMode.INTERNAL_ONLY;

  const showExternal = mode === PromptBuilderMode.FULL || mode === PromptBuilderMode.EXTERNAL_ONLY;

  return (
    <Box
      sx={{
        p: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        margin: '0 auto',
        width: '60%',
      }}
    >
      {showInternal && (
        <>
          <StyledContainer sx={{ borderColor: '#F900D3' }} onClick={onStepByStepCreate}>
            <AutoFixHigh sx={{ color: '#F900D3' }} fontSize="large" />
            <Box>
              <Typography
                sx={{ marginBottom: 2, color: '#F900D3' }}
                variant="subtitle1"
                fontWeight="bold"
              >
                Step-by-step
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Guided wizard that walks you through building a clear, effective prompt. Learn best
                practices as you go and create with confidence.
              </Typography>
            </Box>
          </StyledContainer>
          <Typography variant="subtitle1" sx={{ margin: '0 auto' }}>
            OR
          </Typography>
          <StyledContainer sx={{ borderColor: '#0047FF' }} onClick={onQuickCreate}>
            <Create sx={{ color: '#0047FF' }} fontSize="large" color="primary" />
            <Box>
              <Typography
                sx={{ marginBottom: 2, color: '#0047FF' }}
                variant="subtitle1"
                fontWeight="bold"
              >
                Quick Create
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Jump straight in to draft a custom prompt. Get instant AI suggestions to refine it
                or start from pre-made prompts and tweak as needed.
              </Typography>
            </Box>
          </StyledContainer>
        </>
      )}
      {showInternal && showExternal && (
        <Typography variant="subtitle1" sx={{ margin: '0 auto' }}>
          OR
        </Typography>
      )}
      {showExternal && (
        <StyledContainer sx={{ borderColor: '#0047FF' }} onClick={onCreateExternalPrompt}>
          <Create sx={{ color: '#0047FF' }} fontSize="large" color="primary" />
          <Box>
            <Typography
              sx={{ marginBottom: 2, color: '#0047FF' }}
              variant="subtitle1"
              fontWeight="bold"
            >
              External Prompt
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Bring in an existing prompt from outside of Nigel. After you save it, it will show up
              in the new Nigel library.
            </Typography>
          </Box>
        </StyledContainer>
      )}
    </Box>
  );
};

export default PromptBuilder;
